<template>
  <MarketplaceCard
    :hide-toggle="true"
    description="Dialpad is a cloud-based VoIP phone system. Connect it to sync calls, recordings, and voicemails with your customers."
    :error-text="errorText"
    :notice-text="noticeText"
    notice-type="info"
    learn-more-link="https://www.dialpad.com/"
    cy-selector="marketplaceDialpad"
  >
    <template #logo>
      <div class="w-full d-flex align-center h-12">
        <DialpadLogo class="h-8 my-auto" data-cy="dialpad-logo" />
      </div>
    </template>
    <template #configure>
      <div class="link" data-cy="setup-information-link" @click="openModal">
        Set Up Information
      </div>
      <div
        v-if="isConnected"
        class="link"
        data-cy="manage-users-link"
        @click="mappingsOpened = true"
      >
        Manage Users
      </div>
    </template>
    <template #modal>
      <Modal
        v-if="modalOpened"
        title="Set Up Information - Dialpad"
        submit-btn-text="Connect"
        delete-btn-text="Disconnect"
        :no-submit="isConnected"
        :saving="isSaving"
        :deleting="isDeleting"
        v-on="disconnectListener"
        @submit="connect"
        @hide-modal="closeModal"
      >
        <div class="d-flex flex-column gap-3">
          <div data-cy="dialpad-connection-header">
            <span
              class="d-inline-flex align-items-center gap-2 rounded-full px-2.5 py-1 text-xs font-medium"
              :class="connectionStatus.tone"
              data-cy="dialpad-connection-status"
            >
              <span class="dialpad-status-dot" :class="connectionStatus.dot" />
              {{ connectionStatus.label }}
            </span>
            <p
              v-if="isConnected"
              class="mt-2 mb-0 text-sm text-gray-500"
              data-cy="dialpad-connected-copy"
            >
              Dialpad is connected. Disconnecting removes the stored credentials
              and stops call sync.
            </p>
            <p
              v-else-if="persisted"
              class="mt-2 mb-0 text-sm text-gray-500"
              data-cy="dialpad-failing-copy"
            >
              Dialpad stopped accepting this connection. Connect again to
              restore call sync, or disconnect to remove it.
            </p>
            <p
              v-else
              class="mt-2 mb-0 text-sm text-gray-500"
              data-cy="dialpad-connect-copy"
            >
              Connect your Dialpad account to start syncing calls.
            </p>
          </div>

          <Alert
            type="info"
            class="dialpad-callout"
            data-cy="dialpad-recording-notice"
          >
            <span class="d-block text-sm font-medium text-gray-900">
              Turn on call recording in Dialpad
            </span>
            <span class="d-block mt-1 text-xs text-gray-700">
              Phone Calls can only play a call Dialpad recorded. Turn on
              automatic recording so every call has one.
            </span>
            <button
              type="button"
              data-cy="dialpad-recording-help"
              class="link d-block mt-1 p-0 text-xs bg-transparent border-0"
              @click="helpTopic = 'recording'"
            >
              Where to find it in Dialpad
            </button>
          </Alert>

          <Alert
            type="info"
            class="dialpad-callout"
            data-cy="dialpad-transcript-notice"
          >
            <span class="d-block text-sm font-medium text-gray-900">
              Turn on Dialpad AI for transcripts
            </span>
            <span class="d-block mt-1 text-xs text-gray-700">
              A call's transcript shows within 2-3 minutes after it ends. If you
              don't see one by then, turn on Dialpad AI.
            </span>
            <button
              type="button"
              data-cy="dialpad-transcript-help"
              class="link d-block mt-1 p-0 text-xs bg-transparent border-0"
              @click="helpTopic = 'transcript'"
            >
              Where to find it in Dialpad
            </button>
          </Alert>
        </div>

        <DialpadHelpDrawer
          v-if="helpTopic"
          :topic="helpTopic"
          @close="helpTopic = null"
        />
      </Modal>
      <DialpadUserMappings
        v-if="mappingsOpened && isConnected"
        @close="mappingsOpened = false"
      />
    </template>
  </MarketplaceCard>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";

import Modal from "@/components/Modal.vue";
import Alert from "@/components/Seranova/Alert/Alert.vue";
import DialpadLogo from "@/images/dialpad_logo.svg?component";
import {
  openConnectPopup,
  waitForPopupClose
} from "@/Marketplace/connectPopup";
import DialpadHelpDrawer, {
  type DialpadHelpTopic
} from "./DialpadHelpDrawer.vue";
import DialpadUserMappings from "./DialpadUserMappings.vue";
import MarketplaceCard from "./MarketplaceCard.vue";

type DialpadStatus = "not_connected" | "connected" | "failing";

interface DialpadConfig {
  persisted: boolean;
  status: DialpadStatus;
}

const POPUP_NAME = "dialpad-connect";
const POPUP_BLOCKED_TEXT =
  "Your browser blocked the Dialpad window. Allow pop-ups for this site and try again.";

const FAILING_TEXT =
  "Dialpad connection is failing. Connect again to restore call sync.";

const store = useStore();

const modalOpened = ref(false);
const mappingsOpened = ref(false);
const helpTopic = ref<DialpadHelpTopic | null>(null);
const isSaving = ref(false);
const isDeleting = ref(false);

const config = computed<DialpadConfig>(() => store.state.dialpad.config);
const persisted = computed(() => config.value.persisted);
const isConnected = computed(() => config.value.status === "connected");
// Modal shows Disconnect only when a delete listener is bound. An object keeps the
// listener's key camel-cased (onDelete), which `:on-delete` does not, so Modal's
// $emit("delete") actually reaches it.
const disconnectListener = computed(() =>
  persisted.value ? { delete: disconnect } : {}
);
const errorText = computed(() =>
  config.value.status === "failing" ? FAILING_TEXT : ""
);
const noticeText = computed(() => (isConnected.value ? "Connected" : ""));
const connectionStatus = computed(() => {
  if (isConnected.value) {
    return {
      label: "Connected",
      dot: "bg-green-500",
      tone: "bg-green-50 text-green-700"
    };
  }
  if (persisted.value) {
    return {
      label: "Reconnect Needed",
      dot: "bg-yellow-500",
      tone: "bg-yellow-50 text-yellow-600"
    };
  }
  return {
    label: "Not Connected",
    dot: "bg-gray-400",
    tone: "bg-gray-100 text-gray-600"
  };
});

onMounted(() => store.dispatch("fetchDialpad"));

function openModal() {
  modalOpened.value = true;
}

function closeModal() {
  modalOpened.value = false;
  helpTopic.value = null;
}

// Closing the window without granting access leaves the modal open.
async function connect() {
  const popup = openConnectPopup(POPUP_NAME);
  if (!popup) {
    window.notify.error(POPUP_BLOCKED_TEXT);
    return;
  }

  isSaving.value = true;
  try {
    popup.location.href = await store.dispatch("getDialpadAuthorizeUrl");
    await waitForPopupClose(popup);
    await store.dispatch("fetchDialpad");
    if (isConnected.value) {
      // Brings up the softphone without waiting for a reload.
      store.dispatch("fetchTenant");
      window.notify.success("Dialpad connected!");
      closeModal();
    }
  } catch {
    popup.close();
    window.notify.error("Unable to connect Dialpad");
  } finally {
    isSaving.value = false;
  }
}

async function disconnect() {
  isDeleting.value = true;
  try {
    await store.dispatch("disconnectDialpad");
    window.notify.success("Dialpad disconnected!");
    closeModal();
  } catch {
    window.notify.error("Unable to disconnect Dialpad");
  } finally {
    isDeleting.value = false;
  }
}
</script>

<style scoped lang="scss">
.dialpad-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  display: inline-block;
}
</style>

<style lang="scss">
// The info badge pinned to the first line, as on the GoHighLevel callouts. Tailwind
// runs with important: true here.
div.dialpad-callout {
  align-items: flex-start !important;

  > svg {
    align-self: flex-start !important;
    height: 1.125rem !important;
    width: 1.125rem !important;
    margin-top: 0.0625rem;
    flex-shrink: 0;
  }
}
</style>
